import React, { useEffect, useRef } from 'react';
import '../styles/M3Dialog.css';

/**
 * M3Dialog — Material Design 3 dialog.
 *
 * @param {boolean} open
 * @param {ReactNode} children — title, body, and/or actions
 * @param {Function} onClose
 * @param {string} [title] — legacy prop; also set via <M3Dialog.Title>
 * @param {string} [size] — 'small' (360px) | 'medium' (420px, default) | 'large' (560px) | 'wide' (720px)
 */
export default function M3Dialog({ open, children, onClose, title, size = 'medium' }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Focus the dialog surface on open
  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [open]);

  if (!open) return null;

  const sizeMap = { small: '360px', medium: '420px', large: '560px', wide: '720px' };

  return (
    <div
      className="m3-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`m3-dialog m3-dialog-${size}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Dialog title. */
export function DialogTitle({ children }) {
  return (
    <div className="m3-dialog-header">
      <h2 className="m3-dialog-title">{children}</h2>
    </div>
  );
}

/** Dialog body (flex content area). */
export function DialogBody({ children }) {
  return <div className="m3-dialog-body">{children}</div>;
}

/** Dialog actions (footer buttons). */
export function DialogActions({ primary, secondary }) {
  return (
    <div className="m3-dialog-footer">
      {secondary && (
        <button type="button" className="btn btn-ghost m3-dialog-btn" onClick={secondary.onClick}>
          {secondary.label}
        </button>
      )}
      {primary && (
        <button
          type="button"
          className={`btn ${
            primary.color === 'error' ? 'btn-danger' : 'btn-primary'
          } m3-dialog-btn`}
          onClick={primary.onClick}
        >
          {primary.label}
        </button>
      )}
    </div>
  );
}

M3Dialog.Title = DialogTitle;
M3Dialog.Body = DialogBody;
M3Dialog.Actions = DialogActions;
